import { type Maybe, WorkflowObjectTypes } from '@generated/graphql';

type ContextWithCurrentUserId = { currentUserId?: string };

type HydrateWorkflowCompletionContextInput<
  TContext extends ContextWithCurrentUserId,
> = {
  context: TContext;
  objectType?: Maybe<WorkflowObjectTypes>;
  objectId?: Maybe<string>;
  contextAware: boolean;
};

const hydrateWithObjectId = <TContext extends ContextWithCurrentUserId>(
  context: TContext,
  objectId?: Maybe<string>,
) => ({ ...context, currentUserId: objectId ?? undefined });

// ACCOUNT, GOAL and SUB_ACCOUNT keep the stored context whatever the viewer's
// flag says: their objectId is not a user id, and the backend already derived
// currentUserId. With context_aware_workflows on, a USER only gets
// currentUserId when the stored context has none. HOUSEHOLD, any other type,
// and USER with the flag off keep the original behaviour, which writes
// objectId (a client group id for HOUSEHOLD) into currentUserId.
export const hydrateWorkflowCompletionContext = <
  TContext extends ContextWithCurrentUserId,
>({
  context,
  objectType,
  objectId,
  contextAware,
}: HydrateWorkflowCompletionContextInput<TContext>) => {
  switch (objectType) {
    case WorkflowObjectTypes.Account:
    case WorkflowObjectTypes.Goal:
    case WorkflowObjectTypes.SubAccount:
      return context;
    case WorkflowObjectTypes.User:
      return !contextAware || (!context.currentUserId && objectId)
        ? hydrateWithObjectId(context, objectId)
        : context;
    default:
      return hydrateWithObjectId(context, objectId);
  }
};
